import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api/axios';
import toast from 'react-hot-toast';
import { Plus, Search, Eye, ChevronUp, ChevronDown, X } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const emptyForm = { name: '', gstNo: '', aadharCard: '', address: '', email: '', phone: '', organization: '', openTime: '', closeTime: '' };

export default function OutletsPage() {
  const navigate = useNavigate();
  const { hasRole } = useAuth();
  const [outlets, setOutlets] = useState([]);
  const [orgs, setOrgs] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editId, setEditId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [sortKey, setSortKey] = useState(null); // 'status' | null (default: OFF last)
  const [sortDir, setSortDir] = useState('asc');
  const [credInfo, setCredInfo] = useState(null);

  // Organization can view outlets but cannot add or edit them
  // Organization can edit/manage its outlets (like admin) but cannot add new ones
  const canEdit = hasRole('super_admin', 'outlet_owner', 'organization');
  const canAdd = hasRole('super_admin', 'outlet_owner');

  const fetchData = async () => {
    const [outletRes, orgRes] = await Promise.all([
      api.get('/outlets'),
      api.get('/organizations'),
    ]);
    setOutlets(outletRes.data);
    setOrgs(orgRes.data);
  };

  useEffect(() => { fetchData(); }, []);

  const q = search.trim().toLowerCase();
  const searchedOutlets = !q ? outlets : outlets.filter((o) => {
    const fields = [
      o.outletId,
      o.name,
      o.organization?.name,
      o.phone,
      o.phone ? `+91 ${o.phone}` : '',
      o.phone ? `+91${o.phone}` : '',
      o.email,
      o.address,
    ];
    return fields.filter(Boolean).some((v) => String(v).toLowerCase().includes(q));
  });

  const filteredOutlets = [...searchedOutlets].sort((a, b) => {
    if (sortKey === 'status') {
      const av = a.isActive === false ? 1 : 0;
      const bv = b.isActive === false ? 1 : 0;
      return sortDir === 'asc' ? av - bv : bv - av;
    }
    // default: active (ON) first, inactive (OFF) at the bottom
    return (a.isActive === false ? 1 : 0) - (b.isActive === false ? 1 : 0);
  });

  const toggleSort = (key) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  const openCreate = () => { setForm(emptyForm); setEditId(null); setShowModal(true); };
  const attemptCloseCreate = () => {
    if (window.confirm('Close this form? Any unsaved changes will be lost.')) {
      setShowModal(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await api.post('/outlets', form);
      const login = res.data.login;
      setShowModal(false);
      if (login?.emailSent) {
        toast.success('Outlet created. Login credentials emailed.');
      } else if (login?.password) {
        setCredInfo({ email: login.email, password: login.password, note: login.note });
      } else {
        toast.success('Outlet created');
      }
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Error saving outlet');
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = async (o) => {
    const msg = o.isActive === false
      ? 'Turn ON (activate) this outlet?'
      : 'Turn OFF (deactivate) this outlet?';
    if (!window.confirm(msg)) return;
    try {
      const res = await api.patch(`/outlets/${o._id}/toggle`);
      toast.success(res.data.isActive ? 'Outlet activated' : 'Outlet deactivated');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Error updating status');
    }
  };

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h2>Outlets</h2>
          <p className="text-muted">List of outlets - {outlets.length}</p>
        </div>
        <div className="header-actions">
          <div className="search-bar">
            <Search size={18} className="search-icon" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search outlets by name, ID, organization, address..."
            />
          </div>
          {canAdd && (
            <button className="btn btn-primary" onClick={openCreate}>
              <Plus size={16} /> Add Outlet
            </button>
          )}
        </div>
      </div>

      <div className="table-container table-scroll">
        <table className="table">
          <thead>
            <tr>
              <th>Outlet ID</th>
              <th>Outlet Name</th>
              <th>Organization</th>
              <th>Mobile</th>
              <th>Email</th>
              <th>View Details</th>
              {canEdit && (
                <th className="th-sortable" onClick={() => toggleSort('status')}>
                  Status
                  <span className="sort-carets">
                    <ChevronUp size={11} className={sortKey === 'status' && sortDir === 'asc' ? 'active' : ''} />
                    <ChevronDown size={11} className={sortKey === 'status' && sortDir === 'desc' ? 'active' : ''} />
                  </span>
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {filteredOutlets.map((o) => (
              <tr key={o._id} className={o.isActive === false ? 'row-disabled' : ''}>
                <td><span className="badge outlet-id-badge">{o.outletId}</span></td>
                <td><strong>{o.name}</strong></td>
                <td>{o.organization?.name || '—'}</td>
                <td>{o.phone ? `+91 ${o.phone}` : '—'}</td>
                <td>{o.email || '—'}</td>
                <td>
                  <button className="btn btn-sm btn-outline" onClick={() => navigate(`/outlets/${o._id}`)}>
                    <Eye size={13} /> View
                  </button>
                </td>
                {canEdit && (
                  <td>
                    <button
                      type="button"
                      className={`pill-toggle ${o.isActive === false ? 'off' : 'on'}`}
                      onClick={() => handleToggle(o)}
                      title={o.isActive === false ? 'Turn ON (activate)' : 'Turn OFF (deactivate)'}
                      aria-pressed={o.isActive !== false}
                    >
                      <span className="pill-toggle-label">{o.isActive === false ? 'OFF' : 'ON'}</span>
                      <span className="pill-toggle-knob" />
                    </button>
                  </td>
                )}
              </tr>
            ))}
            {filteredOutlets.length === 0 && (
              <tr><td colSpan={canEdit ? 7 : 6} className="empty-state">
                {outlets.length === 0 ? 'No outlets yet. Create your first outlet.' : 'No outlets match your search.'}
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Credentials fallback modal (when email could not be sent) */}
      {credInfo && (
        <div className="modal-overlay" onClick={() => setCredInfo(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h3>Outlet Login Created</h3>
            <p className="info-banner">{credInfo.note}</p>
            <div className="staff-detail-view">
              <div className="detail-row">
                <span className="detail-label">Login Email</span>
                <span className="detail-value">{credInfo.email}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Password</span>
                <span className="detail-value" style={{ fontFamily: 'monospace', fontWeight: 700 }}>{credInfo.password}</span>
              </div>
            </div>
            <div className="modal-actions">
              <button
                className="btn btn-outline"
                onClick={() => { navigator.clipboard?.writeText(`Email: ${credInfo.email}\nPassword: ${credInfo.password}`); toast.success('Copied'); }}
              >
                Copy
              </button>
              <button className="btn btn-primary" onClick={() => setCredInfo(null)}>Done</button>
            </div>
          </div>
        </div>
      )}

      {showModal && (
        <div className="modal-overlay">
          <div className="modal modal-lg" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header-row">
              <h3>Create Outlet</h3>
              <button className="btn btn-sm btn-outline btn-icon" onClick={attemptCloseCreate} title="Close">
                <X size={16} />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="form-grid">

              <div className="form-group">
                <label>Outlet Name *</label>
                <input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  required
                />
              </div>

              <div className="form-group">
                <label>Organization *</label>
                <select
                  value={form.organization}
                  onChange={(e) => setForm({ ...form, organization: e.target.value })}
                  required
                >
                  <option value="">— Select Organization —</option>
                  {orgs.map((o) => <option key={o._id} value={o._id}>{o.name}</option>)}
                </select>
              </div>

              <div className="form-group">
                <label>Email * <span className="text-muted">(used as login)</span></label>
                <input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  required
                />
              </div>

              <div className="form-group">
                <label>Mobile Number *</label>
                <div className="input-group">
                  <span className="input-prefix">+91</span>
                  <input
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value.replace(/\D/g, '').slice(0, 10) })}
                    maxLength={10}
                    inputMode="numeric"
                    required
                  />
                </div>
              </div>

              <div className="form-group">
                <label>GST Number</label>
                <input
                  value={form.gstNo}
                  onChange={(e) => setForm({ ...form, gstNo: e.target.value.toUpperCase().slice(0, 15) })}
                  maxLength={15}
                />
              </div>

              <div className="form-group">
                <label>Aadhar Card Number</label>
                <input
                  value={form.aadharCard}
                  onChange={(e) => setForm({ ...form, aadharCard: e.target.value.replace(/\D/g, '').slice(0, 12).replace(/(\d{4})(?=\d)/g, '$1-') })}
                  maxLength={14}
                  inputMode="numeric"
                  placeholder="0000-0000-0000"
                />
              </div>

              <div className="form-group">
                <label>Outlet Opening Time</label>
                <input
                  type="time"
                  value={form.openTime}
                  onChange={(e) => setForm({ ...form, openTime: e.target.value })}
                />
              </div>

              <div className="form-group">
                <label>Outlet Closing Time</label>
                <input
                  type="time"
                  value={form.closeTime}
                  onChange={(e) => setForm({ ...form, closeTime: e.target.value })}
                />
              </div>

              <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                <label>Address *</label>
                <textarea
                  value={form.address}
                  onChange={(e) => setForm({ ...form, address: e.target.value })}
                  required
                  rows={2}
                />
              </div>

              <div className="modal-actions" style={{ gridColumn: '1 / -1' }}>
                <button type="button" className="btn btn-outline" onClick={attemptCloseCreate}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={loading}>
                  {loading ? 'Saving...' : 'Create Outlet'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
